package org.zq.linkgame.ui.screens

import androidx.compose.animation.core.*
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import org.zq.linkgame.ui.components.AppText
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import org.zq.linkgame.sound.SoundManager
import org.zq.linkgame.ui.components.*
import org.zq.linkgame.ui.model.*
import org.zq.linkgame.ui.theme.*

/**
 * Get localized difficulty name
 */
@Composable
fun Difficulty.localizedName(): String {
    val strings = LocalStrings.current
    return when (this) {
        Difficulty.EASY -> strings.difficultyEasy
        Difficulty.HARD -> strings.difficultyHard
        Difficulty.EXPERT -> strings.difficultyExpert
        Difficulty.MASTER -> strings.difficultyMaster
    }
}

/**
 * Get localized prop name
 */
@Composable
fun GameProp.localizedName(): String {
    val strings = LocalStrings.current
    return when (this) {
        GameProp.HINT -> strings.hint
        GameProp.BOMB -> strings.bomb
        GameProp.SHUFFLE -> strings.shuffle
    }
}

/**
 * Game screen where the actual matching game is played
 * Contains: Level info, game grid, props panel, and hint area
 */
@Composable
fun GameScreen(
    levelId: Int,
    onClose: () -> Unit,
    backgroundTheme: BackgroundTheme = BackgroundTheme.SKY,
    modifier: Modifier = Modifier
) {
    // Difficulty selection state
    var selectedDifficulty by remember { mutableStateOf<Difficulty?>(null) }
    
    var gameState by remember(selectedDifficulty) {
        mutableStateOf(
            selectedDifficulty?.let { SampleData.createSampleGameState(levelId, it) }
        )
    }
    
    // Connection line path state (list of points for the path)
    var connectionPath by remember { mutableStateOf<List<CellPosition>?>(null) }
    var showLine by remember { mutableStateOf(false) }
    
    // Show completion dialog
    var showCompletionDialog by remember { mutableStateOf(false) }
    
    // Animation for line disappearing
    LaunchedEffect(connectionPath) {
        if (connectionPath != null) {
            showLine = true
            delay(500) // Show line for 500ms
            showLine = false
            connectionPath = null
        }
    }
    
    // Check for game completion
    LaunchedEffect(gameState?.isGameComplete) {
        if (gameState?.isGameComplete == true) {
            showCompletionDialog = true
        }
    }
    
    // Show difficulty selection if not selected
    if (selectedDifficulty == null) {
        DifficultySelectScreen(
            onDifficultySelected = { difficulty ->
                selectedDifficulty = difficulty
            },
            onClose = onClose,
            backgroundTheme = backgroundTheme,
            modifier = modifier
        )
        return
    }
    
    // Game state should not be null at this point
    val currentGameState = gameState ?: return
    
    // Show completion dialog
    if (showCompletionDialog) {
        LevelCompleteDialog(
            currentLevel = currentGameState.currentLevelIndex,
            levelsToPass = currentGameState.levelsToPass,
            difficulty = currentGameState.difficulty,
            onNextLevel = {
                showCompletionDialog = false
                // Create new game state with updated level index
                val newLevelIndex = currentGameState.currentLevelIndex + currentGameState.levelsToPass
                gameState = SampleData.createSampleGameState(
                    levelId = levelId,
                    difficulty = selectedDifficulty!!
                ).copy(currentLevelIndex = newLevelIndex)
            },
            onClose = onClose
        )
        return
    }
    
    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        Color(backgroundTheme.topColor),
                        Color(backgroundTheme.bottomColor)
                    )
                )
            )
    ) {
        // Use aspect ratio to determine layout, not fixed width
        val isLandscape = maxWidth > maxHeight
        
        // Collapsed state for hiding top UI
        var isCollapsed by remember { mutableStateOf(false) }
        
        // No connectable pairs dialog state
        var showNoMatchDialog by remember { mutableStateOf(false) }
        val strings = LocalStrings.current
        
        // Show no match dialog
        if (showNoMatchDialog) {
            androidx.compose.material3.AlertDialog(
                onDismissRequest = { showNoMatchDialog = false },
                title = { AppText(strings.hint) },
                text = { AppText(strings.noConnectablePairs) },
                confirmButton = {
                    androidx.compose.material3.TextButton(
                        onClick = { showNoMatchDialog = false }
                    ) {
                        AppText(strings.confirm)
                    }
                }
            )
        }
        
        if (isLandscape) {
            // Landscape layout: Game on left, props on right
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Left side - Game Grid (main area) - centered
                GameGrid(
                    gameState = currentGameState,
                    connectionPath = if (showLine) connectionPath else null,
                    onTileClick = { position ->
                        val result = handleTileClickWithPath(currentGameState, position)
                        gameState = result.first
                        if (result.second != null) {
                            connectionPath = result.second
                            SoundManager.playMatchSound()
                        }
                    },
                    backgroundTheme = backgroundTheme,
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                )
                
                // Right side - Info and Props (compact vertical) - collapsible
                if (!isCollapsed) {
                    Spacer(modifier = Modifier.width(8.dp))
                    
                    Column(
                        modifier = Modifier
                            .width(100.dp)
                            .fillMaxHeight(),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.SpaceBetween
                    ) {
                        // Top: Level info and close button
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            // Difficulty badge
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(6.dp))
                                    .background(
                                        when (currentGameState.difficulty) {
                                            Difficulty.EASY -> Green.copy(alpha = 0.2f)
                                            Difficulty.HARD -> OrangeRed.copy(alpha = 0.2f)
                                            Difficulty.EXPERT -> Color.Red.copy(alpha = 0.2f)
                                            Difficulty.MASTER -> Color(0xFF8B00FF).copy(alpha = 0.2f)
                                        }
                                    )
                                    .padding(horizontal = 8.dp, vertical = 4.dp)
                            ) {
                                AppText(
                                    text = currentGameState.difficulty.localizedName(),
                                    style = MaterialTheme.typography.bodySmall,
                                    color = when (currentGameState.difficulty) {
                                        Difficulty.EASY -> DeepGreen
                                        Difficulty.HARD -> OrangeRed
                                        Difficulty.EXPERT -> Color.Red
                                        Difficulty.MASTER -> Color(0xFF8B00FF)
                                    }
                                )
                            }
                            
                            Spacer(modifier = Modifier.height(4.dp))
                            
                            AppText(
                                text = LocalStrings.current.level.replace("%d", currentGameState.currentLevelIndex.toString()),
                                style = MaterialTheme.typography.titleSmall,
                                color = Color(backgroundTheme.textColor)
                            )
                            
                            Spacer(modifier = Modifier.height(8.dp))
                            
                            // Close button
                            CircleIconButton(
                                onClick = onClose,
                                size = 32.dp,
                                backgroundColor = VeryLightGray
                            ) {
                                CloseIcon(size = 16.dp, color = DarkGray)
                            }
                        }
                        
                        // Middle: Props (vertical)
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            GameProp.entries.forEach { prop ->
                                val count = currentGameState.props[prop] ?: 0
                                CompactPropItem(
                                    prop = prop,
                                    count = count,
                                    onClick = {
                                        val result = handlePropClick(currentGameState, prop)
                                        gameState = result.first
                                        if (result.second) showNoMatchDialog = true
                                    },
                                    backgroundTheme = backgroundTheme
                                )
                            }
                        }
                        
                        // Bottom spacer
                        Spacer(modifier = Modifier.height(8.dp))
                    }
                }
            }
            
            // Floating toggle button for landscape - bottom right, avoid game grid
            Box(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(bottom = 16.dp, end = if (isCollapsed) 16.dp else 116.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(28.dp)
                        .clip(RoundedCornerShape(6.dp))
                        .background(Color.White.copy(alpha = 0.7f))
                        .clickable { isCollapsed = !isCollapsed },
                    contentAlignment = Alignment.Center
                ) {
                    AppText(
                        text = if (isCollapsed) "◀" else "▶",
                        style = MaterialTheme.typography.bodySmall,
                        color = DarkGray
                    )
                }
            }
        } else {
            // Narrow screen layout (stacked)
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp)
            ) {
                // Top bar and props - collapsible
                if (!isCollapsed) {
                    // Top bar
                    GameTopBar(
                        levelName = currentGameState.difficulty.localizedName(),
                        currentLevelIndex = currentGameState.currentLevelIndex,
                        difficulty = currentGameState.difficulty,
                        backgroundTheme = backgroundTheme,
                        onClose = onClose,
                        modifier = Modifier.fillMaxWidth()
                    )
                    
                    Spacer(modifier = Modifier.height(8.dp))
                    
                    // Props row (horizontal for narrow screens)
                    PropsRow(
                        gameState = currentGameState,
                        onPropClick = { prop ->
                            val result = handlePropClick(currentGameState, prop)
                            gameState = result.first
                            if (result.second) showNoMatchDialog = true
                        },
                        modifier = Modifier.fillMaxWidth()
                    )
                    
                    Spacer(modifier = Modifier.height(8.dp))
                }
                
                // Game Grid - centered
                GameGrid(
                    gameState = currentGameState,
                    connectionPath = if (showLine) connectionPath else null,
                    onTileClick = { position ->
                        val result = handleTileClickWithPath(currentGameState, position)
                        gameState = result.first
                        if (result.second != null) {
                            connectionPath = result.second
                            SoundManager.playMatchSound()
                        }
                    },
                    backgroundTheme = backgroundTheme,
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxWidth()
                )
            }
            
            // Floating toggle button for portrait - top center
            Box(
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .padding(top = 24.dp)
            ) {
                Box(
                    modifier = Modifier
                        .size(28.dp)
                        .clip(RoundedCornerShape(6.dp))
                        .background(Color.White.copy(alpha = 0.7f))
                        .clickable { isCollapsed = !isCollapsed },
                    contentAlignment = Alignment.Center
                ) {
                    AppText(
                        text = if (isCollapsed) "▼" else "▲",
                        style = MaterialTheme.typography.bodySmall,
                        color = DarkGray
                    )
                }
            }
        }
    }
}

/**
 * Top bar with level info and close button
 */
@Composable
private fun GameTopBar(
    levelName: String,
    currentLevelIndex: Int,
    difficulty: Difficulty,
    backgroundTheme: BackgroundTheme,
    onClose: () -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .padding(vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Difficulty badge
        Box(
            modifier = Modifier
                .clip(RoundedCornerShape(8.dp))
                .background(
                    when (difficulty) {
                        Difficulty.EASY -> Green.copy(alpha = 0.2f)
                        Difficulty.HARD -> OrangeRed.copy(alpha = 0.2f)
                        Difficulty.EXPERT -> Color.Red.copy(alpha = 0.2f)
                        Difficulty.MASTER -> Color(0xFF8B00FF).copy(alpha = 0.2f)
                    }
                )
                .padding(horizontal = 8.dp, vertical = 4.dp)
        ) {
            AppText(
                text = difficulty.localizedName(),
                style = MaterialTheme.typography.bodyMedium,
                color = when (difficulty) {
                    Difficulty.EASY -> DeepGreen
                    Difficulty.HARD -> OrangeRed
                    Difficulty.EXPERT -> Color.Red
                    Difficulty.MASTER -> Color(0xFF8B00FF)
                }
            )
        }
        
        Spacer(modifier = Modifier.width(12.dp))
        
        // Level info
        AppText(
            text = "关卡 $currentLevelIndex",
            style = MaterialTheme.typography.titleLarge,
            color = Color(backgroundTheme.textColor)
        )
        
        Spacer(modifier = Modifier.weight(1f))
        
        Spacer(modifier = Modifier.width(16.dp))
        
        // Close button - circular and smaller
        CircleIconButton(
            onClick = onClose,
            size = 36.dp,
            backgroundColor = VeryLightGray
        ) {
            CloseIcon(
                size = 18.dp,
                color = DarkGray
            )
        }
    }
}

/**
 * Game grid with tiles - Adaptive sizing based on available space
 */
@Composable
private fun GameGrid(
    gameState: GameState,
    connectionPath: List<CellPosition>?,
    onTileClick: (CellPosition) -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    BoxWithConstraints(
        modifier = modifier,
        contentAlignment = Alignment.Center
    ) {
        // Auto-swap rows and cols based on screen orientation
        // Wide screen (landscape): use fewer rows, more cols
        // Tall screen (portrait): use more rows, fewer cols
        val isWideScreen = maxWidth > maxHeight
        val baseRows = gameState.rows
        val baseCols = gameState.cols
        
        // Check if we need to swap dimensions
        val needsSwap = if (isWideScreen) {
            baseRows > baseCols  // Wide screen but more rows than cols, need swap
        } else {
            baseCols > baseRows  // Tall screen but more cols than rows, need swap
        }
        
        val (rows, cols) = if (needsSwap) {
            baseCols to baseRows
        } else {
            baseRows to baseCols
        }
        
        // Convert display position to data position
        fun toDataPosition(displayRow: Int, displayCol: Int): CellPosition {
            return if (needsSwap) {
                CellPosition(displayCol, displayRow)
            } else {
                CellPosition(displayRow, displayCol)
            }
        }
        
        // Convert data position to display position for line drawing
        fun toDisplayPosition(dataPos: CellPosition): Pair<Int, Int> {
            return if (needsSwap) {
                dataPos.col to dataPos.row
            } else {
                dataPos.row to dataPos.col
            }
        }
        
        val spacing = 8.dp
        val padding = 12.dp
        val density = LocalDensity.current
        
        // Calculate the maximum cell size that fits within available space
        // Available space for grid content (excluding padding)
        val availableWidth = maxWidth - padding * 2
        val availableHeight = maxHeight - padding * 2
        
        // Calculate cell size based on available space
        // Account for spacing between cells
        val maxCellWidth = (availableWidth - spacing * (cols - 1)) / cols
        val maxCellHeight = (availableHeight - spacing * (rows - 1)) / rows
        
        // Use the smaller dimension to ensure cells are square and fit within bounds
        val cellSize = minOf(maxCellWidth, maxCellHeight)
        
        // Calculate exact grid content size
        val gridContentWidth = cellSize * cols + spacing * (cols - 1)
        val gridContentHeight = cellSize * rows + spacing * (rows - 1)
        val totalWidth = gridContentWidth + padding * 2
        val totalHeight = gridContentHeight + padding * 2
        
        // Calculate pixel values for line drawing
        val cellSizePx = with(density) { cellSize.toPx() }
        val spacingPx = with(density) { spacing.toPx() }
        val paddingPx = with(density) { padding.toPx() }
        
        // Calculate exact grid dimensions for centered display
        val gridBox = Modifier
            .size(totalWidth, totalHeight)
            .clip(RoundedCornerShape(16.dp))
            .background(Color.Transparent)
        
        Box(
            modifier = gridBox
        ) {
            Column(
                modifier = Modifier.padding(padding),
                verticalArrangement = Arrangement.spacedBy(spacing),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                for (displayRow in 0 until rows) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(spacing)
                    ) {
                        for (displayCol in 0 until cols) {
                            // Convert display position to data position
                            val dataPosition = toDataPosition(displayRow, displayCol)
                            val tile = gameState.tiles.find { 
                                it.position == dataPosition 
                            }
                            
                            if (tile != null) {
                                GameTileCell(
                                    patternType = tile.patternType,
                                    isSelected = dataPosition in gameState.selectedTiles,
                                    isMatched = tile.isMatched,
                                    onClick = { onTileClick(dataPosition) },
                                    cellSize = cellSize
                                )
                            } else {
                                // Empty cell
                                Box(
                                    modifier = Modifier
                                        .size(cellSize)
                                        .clip(RoundedCornerShape(8.dp))
                                        .background(Color.Transparent)
                                )
                            }
                        }
                    }
                }
            }
            
            // Draw connection path overlay
            if (connectionPath != null && connectionPath.size >= 2) {
                // Animate line alpha
                val lineAlpha by animateFloatAsState(
                    targetValue = 1f,
                    animationSpec = tween(durationMillis = 200)
                )
                
                Canvas(
                    modifier = Modifier.matchParentSize()
                ) {
                    // Calculate background brightness to choose contrasting line color
                    val bgColor = Color(backgroundTheme.topColor)
                    val brightness = (bgColor.red * 299 + bgColor.green * 587 + bgColor.blue * 114) / 1000
                    // Dark background -> bright line, Light background -> dark line
                    val lineColor = if (brightness < 0.5f) {
                        Color(0xFF00FFFF).copy(alpha = lineAlpha) // 青色（深色背景用）
                    } else {
                        Color(0xFF0066CC).copy(alpha = lineAlpha) // 深蓝色（浅色背景用）
                    }
                    val strokeWidth = 4.dp.toPx()
                    
                    // Draw lines between consecutive points in the path
                    for (i in 0 until connectionPath.size - 1) {
                        val current = connectionPath[i]
                        val next = connectionPath[i + 1]
                        
                        // Convert data positions to display positions
                        val (currentDisplayRow, currentDisplayCol) = toDisplayPosition(current)
                        val (nextDisplayRow, nextDisplayCol) = toDisplayPosition(next)
                        
                        // Calculate center of each cell using display coordinates
                        val currentX = paddingPx + currentDisplayCol * cellSizePx + currentDisplayCol * spacingPx + cellSizePx / 2
                        val currentY = paddingPx + currentDisplayRow * cellSizePx + currentDisplayRow * spacingPx + cellSizePx / 2
                        val nextX = paddingPx + nextDisplayCol * cellSizePx + nextDisplayCol * spacingPx + cellSizePx / 2
                        val nextY = paddingPx + nextDisplayRow * cellSizePx + nextDisplayRow * spacingPx + cellSizePx / 2
                        
                        drawLine(
                            color = lineColor,
                            start = Offset(currentX, currentY),
                            end = Offset(nextX, nextY),
                            strokeWidth = strokeWidth,
                            cap = StrokeCap.Round
                        )
                    }
                }
            }
        }
    }
}

/**
 * Props panel (vertical layout for wide screens)
 */
@Composable
private fun PropsPanel(
    gameState: GameState,
    onPropClick: (GameProp) -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(16.dp))
            .background(Color.White.copy(alpha = 0.5f))
            .padding(16.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(20.dp)
    ) {
        // Props
        GameProp.entries.forEach { prop ->
            val count = gameState.props[prop] ?: 0
            PropItemCard(
                propName = prop.localizedName(),
                count = count,
                icon = {
                    when (prop) {
                        GameProp.HINT -> HintBulbIcon(size = 50.dp)
                        GameProp.BOMB -> BombIcon(size = 50.dp)
                        GameProp.SHUFFLE -> ShuffleIcon(size = 50.dp)
                    }
                },
                onClick = { onPropClick(prop) }
            )
        }
        
        Spacer(modifier = Modifier.weight(1f))
        
        // Hint area
        HintArea(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f, fill = false)
        )
    }
}

/**
 * Props row (horizontal layout for narrow screens)
 */
@Composable
private fun PropsRow(
    gameState: GameState,
    onPropClick: (GameProp) -> Unit,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White.copy(alpha = 0.5f))
            .padding(12.dp),
        horizontalArrangement = Arrangement.SpaceEvenly,
        verticalAlignment = Alignment.CenterVertically
    ) {
        GameProp.entries.forEach { prop ->
            val count = gameState.props[prop] ?: 0
            PropItemCard(
                propName = prop.localizedName(),
                count = count,
                icon = {
                    when (prop) {
                        GameProp.HINT -> HintBulbIcon(size = 36.dp)
                        GameProp.BOMB -> BombIcon(size = 36.dp)
                        GameProp.SHUFFLE -> ShuffleIcon(size = 36.dp)
                    }
                },
                onClick = { onPropClick(prop) }
            )
        }
    }
}

/**
 * Compact prop item for landscape mode
 */
@Composable
private fun CompactPropItem(
    prop: GameProp,
    count: Int,
    onClick: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(8.dp))
            .background(Color.White.copy(alpha = 0.7f))
            .clickable(enabled = count > 0, onClick = onClick)
            .padding(horizontal = 10.dp, vertical = 8.dp)
            .alpha(if (count > 0) 1f else 0.5f),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Box(
            modifier = Modifier.padding(top = 4.dp, end = 4.dp),
            contentAlignment = Alignment.Center
        ) {
            when (prop) {
                GameProp.HINT -> HintBulbIcon(size = 28.dp)
                GameProp.BOMB -> BombIcon(size = 28.dp)
                GameProp.SHUFFLE -> ShuffleIcon(size = 28.dp)
            }
            if (count > 0) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .offset(x = 8.dp, y = (-8).dp)
                        .size(18.dp)
                        .clip(androidx.compose.foundation.shape.CircleShape)
                        .background(OrangeRed),
                    contentAlignment = Alignment.Center
                ) {
                    AppText(
                        text = count.toString(),
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White
                    )
                }
            }
        }
        AppText(
            text = prop.localizedName(),
            style = MaterialTheme.typography.labelSmall,
            color = Color(backgroundTheme.textColor)
        )
    }
}

/**
 * Difficulty selection screen
 */
@Composable
private fun DifficultySelectScreen(
    onDifficultySelected: (Difficulty) -> Unit,
    onClose: () -> Unit,
    backgroundTheme: BackgroundTheme,
    modifier: Modifier = Modifier
) {
    val strings = LocalStrings.current
    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(
                brush = Brush.verticalGradient(
                    colors = listOf(
                        Color(backgroundTheme.topColor),
                        Color(backgroundTheme.bottomColor)
                    )
                )
            ),
        contentAlignment = Alignment.Center
    ) {
        val isLandscape = maxWidth > maxHeight
        
        if (isLandscape) {
            // Landscape: horizontal layout
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(16.dp),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Left: Title and back button
                Column(
                    modifier = Modifier.padding(end = 24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.Center
                ) {
                    AppText(
                        text = strings.selectDifficulty,
                        style = MaterialTheme.typography.headlineMedium,
                        color = Color(backgroundTheme.textColor)
                    )
                    Spacer(modifier = Modifier.height(16.dp))
                    SecondaryGameButton(
                        text = strings.back,
                        onClick = onClose,
                        modifier = Modifier.width(100.dp)
                    )
                }
                
                // Right: Difficulty cards in 2x2 grid
                Column(
                    verticalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        CompactDifficultyCard(
                            difficulty = Difficulty.EASY,
                            onClick = { onDifficultySelected(Difficulty.EASY) }
                        )
                        CompactDifficultyCard(
                            difficulty = Difficulty.HARD,
                            onClick = { onDifficultySelected(Difficulty.HARD) }
                        )
                    }
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        CompactDifficultyCard(
                            difficulty = Difficulty.EXPERT,
                            onClick = { onDifficultySelected(Difficulty.EXPERT) }
                        )
                        CompactDifficultyCard(
                            difficulty = Difficulty.MASTER,
                            onClick = { onDifficultySelected(Difficulty.MASTER) }
                        )
                    }
                }
            }
        } else {
            // Portrait: vertical layout
            Column(
                modifier = Modifier.padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                AppText(
                    text = strings.selectDifficulty,
                    style = MaterialTheme.typography.headlineMedium,
                    color = Color(backgroundTheme.textColor)
                )
                
                Spacer(modifier = Modifier.height(8.dp))
                
                Difficulty.entries.forEach { difficulty ->
                    DifficultyCard(
                        difficulty = difficulty,
                        onClick = { onDifficultySelected(difficulty) }
                    )
                }
                
                Spacer(modifier = Modifier.height(8.dp))
                
                SecondaryGameButton(
                    text = strings.back,
                    onClick = onClose,
                    modifier = Modifier.width(200.dp)
                )
            }
        }
    }
}

/**
 * Compact difficulty card for landscape mode
 */
@Composable
private fun CompactDifficultyCard(
    difficulty: Difficulty,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val borderColor = when (difficulty) {
        Difficulty.EASY -> DeepGreen
        Difficulty.HARD -> OrangeRed
        Difficulty.EXPERT -> Color.Red
        Difficulty.MASTER -> Color(0xFF8B00FF)
    }
    
    Box(
        modifier = modifier
            .width(140.dp)
            .clip(RoundedCornerShape(12.dp))
            .background(Color.White)
            .border(2.dp, borderColor, RoundedCornerShape(12.dp))
            .clickable(onClick = onClick)
            .padding(12.dp)
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            modifier = Modifier.fillMaxWidth()
        ) {
            AppText(
                text = difficulty.localizedName(),
                style = MaterialTheme.typography.titleMedium,
                color = borderColor,
                maxLines = 1,
                textAlign = TextAlign.Center
            )
            AppText(
                text = "${difficulty.rows}×${difficulty.cols}",
                style = MaterialTheme.typography.bodySmall,
                color = DarkGray,
                maxLines = 1
            )
            AppText(
                text = LocalStrings.current.gainLevels.replace("%d", difficulty.levelsToPass.toString()),
                style = MaterialTheme.typography.labelSmall,
                color = borderColor,
                maxLines = 1
            )
        }
    }
}

/**
 * Difficulty selection card
 */
@Composable
private fun DifficultyCard(
    difficulty: Difficulty,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val backgroundColor = when (difficulty) {
        Difficulty.EASY -> Green.copy(alpha = 0.15f)
        Difficulty.HARD -> OrangeRed.copy(alpha = 0.15f)
        Difficulty.EXPERT -> Color.Red.copy(alpha = 0.15f)
        Difficulty.MASTER -> Color(0xFF8B00FF).copy(alpha = 0.15f)
    }
    
    val borderColor = when (difficulty) {
        Difficulty.EASY -> DeepGreen
        Difficulty.HARD -> OrangeRed
        Difficulty.EXPERT -> Color.Red
        Difficulty.MASTER -> Color(0xFF8B00FF)
    }
    
    Box(
        modifier = modifier
            .width(280.dp)
            .clip(RoundedCornerShape(16.dp))
            .background(Color.White)
            .border(2.dp, borderColor, RoundedCornerShape(16.dp))
            .clickable(onClick = onClick)
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Difficulty badge
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(backgroundColor)
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            ) {
                AppText(
                    text = difficulty.localizedName(),
                    style = MaterialTheme.typography.titleMedium,
                    color = borderColor,
                    maxLines = 1
                )
            }
            
            Spacer(modifier = Modifier.weight(1f))
            
            // Info
            Column(
                horizontalAlignment = Alignment.End
            ) {
                AppText(
                    text = "${difficulty.rows}×${difficulty.cols}",
                    style = MaterialTheme.typography.bodyLarge,
                    color = DarkGray,
                    maxLines = 1
                )
                AppText(
                    text = LocalStrings.current.passLevelsFormat.replace("%d", difficulty.levelsToPass.toString()),
                    style = MaterialTheme.typography.bodyMedium,
                    color = borderColor,
                    maxLines = 1
                )
            }
        }
    }
}

/**
 * Hint area with game rules
 */
@Composable
private fun HintArea(
    modifier: Modifier = Modifier
) {
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .background(HintAreaBg)
            .border(2.dp, DarkGray.copy(alpha = 0.3f), RoundedCornerShape(12.dp))
            .padding(12.dp),
        contentAlignment = Alignment.Center
    ) {
        AppText(
            text = "点击两个相同图案，连线不超过两次转折且无阻挡可消除",
            style = MaterialTheme.typography.bodyLarge,
            color = DarkGray
        )
    }
}

/**
 * Pathfinding for Link Game
 * Finds a valid path between two cells with at most 2 turns
 * Returns the path as a list of CellPositions, or null if no valid path
 */
private fun findPath(
    start: CellPosition,
    end: CellPosition,
    gameState: GameState
): List<CellPosition>? {
    // Create obstacle map (true = blocked by unmatched tile)
    val rows = gameState.rows
    val cols = gameState.cols
    val blocked = Array(rows) { BooleanArray(cols) { false } }
    
    gameState.tiles.forEach { tile ->
        if (!tile.isMatched) {
            blocked[tile.position.row][tile.position.col] = true
        }
    }
    
    // Mark start and end as NOT blocked (we're trying to connect them)
    blocked[start.row][start.col] = false
    blocked[end.row][end.col] = false
    
    // Check if a horizontal line is clear (exclusive of endpoints if they are start/end)
    fun isHorizontalClear(row: Int, col1: Int, col2: Int, excludeStart: Boolean = false, excludeEnd: Boolean = false): Boolean {
        if (row !in 0 until rows) return false
        val minCol = minOf(col1, col2)
        val maxCol = maxOf(col1, col2)
        for (col in minCol..maxCol) {
            if (col !in 0 until cols) return false
            // Skip checking start/end positions
            if (excludeStart && row == start.row && col == start.col) continue
            if (excludeEnd && row == end.row && col == end.col) continue
            if (blocked[row][col]) return false
        }
        return true
    }
    
    // Check if a vertical line is clear (exclusive of endpoints if they are start/end)
    fun isVerticalClear(col: Int, row1: Int, row2: Int, excludeStart: Boolean = false, excludeEnd: Boolean = false): Boolean {
        if (col !in 0 until cols) return false
        val minRow = minOf(row1, row2)
        val maxRow = maxOf(row1, row2)
        for (row in minRow..maxRow) {
            if (row !in 0 until rows) return false
            // Skip checking start/end positions
            if (excludeStart && row == start.row && col == start.col) continue
            if (excludeEnd && row == end.row && col == end.col) continue
            if (blocked[row][col]) return false
        }
        return true
    }
    
    // Check direct line (0 turns)
    fun checkDirectLine(): List<CellPosition>? {
        if (start.row == end.row) {
            // Horizontal line - check cells between start and end
            if (isHorizontalClear(start.row, start.col, end.col, excludeStart = true, excludeEnd = true)) {
                return listOf(start, end)
            }
        }
        if (start.col == end.col) {
            // Vertical line - check cells between start and end
            if (isVerticalClear(start.col, start.row, end.row, excludeStart = true, excludeEnd = true)) {
                return listOf(start, end)
            }
        }
        return null
    }
    
    // Check one turn path (L-shape)
    fun checkOneTurn(): List<CellPosition>? {
        // Try corner at (start.row, end.col)
        val corner1 = CellPosition(start.row, end.col)
        if (corner1 != start && corner1 != end && 
            corner1.row in 0 until rows && corner1.col in 0 until cols &&
            !blocked[corner1.row][corner1.col]) {
            // Check horizontal from start to corner1 (exclude start)
            // Check vertical from corner1 to end (exclude end)
            val hClear = isHorizontalClear(start.row, start.col, end.col, excludeStart = true, excludeEnd = false)
            val vClear = isVerticalClear(end.col, start.row, end.row, excludeStart = false, excludeEnd = true)
            if (hClear && vClear) {
                return listOf(start, corner1, end)
            }
        }
        
        // Try corner at (end.row, start.col)
        val corner2 = CellPosition(end.row, start.col)
        if (corner2 != start && corner2 != end && 
            corner2.row in 0 until rows && corner2.col in 0 until cols &&
            !blocked[corner2.row][corner2.col]) {
            // Check vertical from start to corner2 (exclude start)
            // Check horizontal from corner2 to end (exclude end)
            val vClear = isVerticalClear(start.col, start.row, end.row, excludeStart = true, excludeEnd = false)
            val hClear = isHorizontalClear(end.row, start.col, end.col, excludeStart = false, excludeEnd = true)
            if (vClear && hClear) {
                return listOf(start, corner2, end)
            }
        }
        
        return null
    }
    
    // Check two turn path (Z-shape or U-shape)
    fun checkTwoTurns(): List<CellPosition>? {
        // Try all possible intermediate columns (Z-shape: horizontal, vertical, horizontal)
        for (col in -1..cols) { // -1 and cols are outside the grid (virtual edges)
            if (col == start.col && col == end.col) continue
            
            val mid1 = CellPosition(start.row, col)
            val mid2 = CellPosition(end.row, col)
            
            // Check if mid points are valid (can be outside grid for edge paths)
            val mid1Valid = col in 0 until cols && mid1.row in 0 until rows && !blocked[mid1.row][mid1.col]
            val mid2Valid = col in 0 until cols && mid2.row in 0 until rows && !blocked[mid2.row][mid2.col]
            val isEdgePath = col < 0 || col >= cols
            
            if (mid1Valid && mid2Valid || isEdgePath) {
                // For edge paths, the vertical line goes outside the grid
                if (isEdgePath) {
                    // Check horizontal from start to edge
                    val h1Clear = if (col < 0) {
                        isHorizontalClear(start.row, 0, start.col, excludeStart = true, excludeEnd = false)
                    } else {
                        isHorizontalClear(start.row, start.col, cols - 1, excludeStart = true, excludeEnd = false)
                    }
                    // Check horizontal from edge to end
                    val h2Clear = if (col < 0) {
                        isHorizontalClear(end.row, 0, end.col, excludeStart = false, excludeEnd = true)
                    } else {
                        isHorizontalClear(end.row, end.col, cols - 1, excludeStart = false, excludeEnd = true)
                    }
                    if (h1Clear && h2Clear) {
                        // Return edge path
                        return listOf(start, CellPosition(start.row, if (col < 0) 0 else cols - 1), 
                                     CellPosition(end.row, if (col < 0) 0 else cols - 1), end)
                    }
                } else {
                    val h1Clear = isHorizontalClear(start.row, start.col, col, excludeStart = true, excludeEnd = false)
                    val vClear = isVerticalClear(col, start.row, end.row)
                    val h2Clear = isHorizontalClear(end.row, col, end.col, excludeStart = false, excludeEnd = true)
                    
                    if (h1Clear && vClear && h2Clear) {
                        return listOf(start, mid1, mid2, end)
                    }
                }
            }
        }
        
        // Try all possible intermediate rows (U-shape: vertical, horizontal, vertical)
        for (row in -1..rows) { // -1 and rows are outside the grid (virtual edges)
            if (row == start.row && row == end.row) continue
            
            val mid1 = CellPosition(row, start.col)
            val mid2 = CellPosition(row, end.col)
            
            // Check if mid points are valid
            val mid1Valid = row in 0 until rows && mid1.col in 0 until cols && !blocked[mid1.row][mid1.col]
            val mid2Valid = row in 0 until rows && mid2.col in 0 until cols && !blocked[mid2.row][mid2.col]
            val isEdgePath = row < 0 || row >= rows
            
            if (mid1Valid && mid2Valid || isEdgePath) {
                if (isEdgePath) {
                    // Check vertical from start to edge
                    val v1Clear = if (row < 0) {
                        isVerticalClear(start.col, 0, start.row, excludeStart = true, excludeEnd = false)
                    } else {
                        isVerticalClear(start.col, start.row, rows - 1, excludeStart = true, excludeEnd = false)
                    }
                    // Check vertical from edge to end
                    val v2Clear = if (row < 0) {
                        isVerticalClear(end.col, 0, end.row, excludeStart = false, excludeEnd = true)
                    } else {
                        isVerticalClear(end.col, end.row, rows - 1, excludeStart = false, excludeEnd = true)
                    }
                    if (v1Clear && v2Clear) {
                        val edgeRow = if (row < 0) 0 else rows - 1
                        return listOf(start, CellPosition(edgeRow, start.col),
                                     CellPosition(edgeRow, end.col), end)
                    }
                } else {
                    val v1Clear = isVerticalClear(start.col, start.row, row, excludeStart = true, excludeEnd = false)
                    val hClear = isHorizontalClear(row, start.col, end.col)
                    val v2Clear = isVerticalClear(end.col, row, end.row, excludeStart = false, excludeEnd = true)
                    
                    if (v1Clear && hClear && v2Clear) {
                        return listOf(start, mid1, mid2, end)
                    }
                }
            }
        }
        
        return null
    }
    
    // Try each method in order
    return checkDirectLine() ?: checkOneTurn() ?: checkTwoTurns()
}

/**
 * Handle tile click logic with pathfinding
 * Returns Pair of (new GameState, connection path if matched)
 */
private fun handleTileClickWithPath(
    currentState: GameState,
    clickedPosition: CellPosition
): Pair<GameState, List<CellPosition>?> {
    val clickedTile = currentState.tiles.find { 
        it.position == clickedPosition && !it.isMatched 
    } ?: return Pair(currentState, null)
    
    val selectedTiles = currentState.selectedTiles.toMutableList()
    
    return when {
        // Clicked already selected tile - deselect
        clickedPosition in selectedTiles -> {
            Pair(
                currentState.copy(
                    selectedTiles = selectedTiles.apply { remove(clickedPosition) }
                ),
                null
            )
        }
        // First selection
        selectedTiles.isEmpty() -> {
            Pair(
                currentState.copy(selectedTiles = listOf(clickedPosition)),
                null
            )
        }
        // Second selection
        selectedTiles.size == 1 -> {
            val firstPosition = selectedTiles.first()
            val firstTile = currentState.tiles.find { it.position == firstPosition }
            
            if (firstTile != null && firstTile.patternType == clickedTile.patternType) {
                // Same pattern - check if valid path exists
                val path = findPath(firstPosition, clickedPosition, currentState)
                
                if (path != null) {
                    // Valid path found - match!
                    val updatedTiles = currentState.tiles.map { tile ->
                        if (tile.position == firstPosition || tile.position == clickedPosition) {
                            tile.copy(isMatched = true)
                        } else {
                            tile
                        }
                    }
                    Pair(
                        currentState.copy(
                            tiles = updatedTiles,
                            selectedTiles = emptyList(),
                            matchedPairs = currentState.matchedPairs + 1,
                            score = currentState.score + 100,
                            isGameComplete = currentState.matchedPairs + 1 >= currentState.totalPairs
                        ),
                        path // Return the valid path
                    )
                } else {
                    // No valid path - keep first selection
                    Pair(currentState, null)
                }
            } else {
                // Different pattern - select new tile
                Pair(
                    currentState.copy(selectedTiles = listOf(clickedPosition)),
                    null
                )
            }
        }
        else -> Pair(currentState, null)
    }
}

/**
 * Handle tile click logic with connection line
 * Returns Pair of (new GameState, connection line positions if matched)
 */
private fun handleTileClickWithLine(
    currentState: GameState,
    clickedPosition: CellPosition
): Pair<GameState, Pair<CellPosition, CellPosition>?> {
    val clickedTile = currentState.tiles.find { 
        it.position == clickedPosition && !it.isMatched 
    } ?: return Pair(currentState, null)
    
    val selectedTiles = currentState.selectedTiles.toMutableList()
    
    return when {
        // Clicked already selected tile - deselect
        clickedPosition in selectedTiles -> {
            Pair(
                currentState.copy(
                    selectedTiles = selectedTiles.apply { remove(clickedPosition) }
                ),
                null
            )
        }
        // First selection
        selectedTiles.isEmpty() -> {
            Pair(
                currentState.copy(selectedTiles = listOf(clickedPosition)),
                null
            )
        }
        // Second selection
        selectedTiles.size == 1 -> {
            val firstPosition = selectedTiles.first()
            val firstTile = currentState.tiles.find { it.position == firstPosition }
            
            if (firstTile != null && firstTile.patternType == clickedTile.patternType) {
                // Match found!
                val updatedTiles = currentState.tiles.map { tile ->
                    if (tile.position == firstPosition || tile.position == clickedPosition) {
                        tile.copy(isMatched = true)
                    } else {
                        tile
                    }
                }
                Pair(
                    currentState.copy(
                        tiles = updatedTiles,
                        selectedTiles = emptyList(),
                        matchedPairs = currentState.matchedPairs + 1,
                        score = currentState.score + 100,
                        isGameComplete = currentState.matchedPairs + 1 >= currentState.totalPairs
                    ),
                    Pair(firstPosition, clickedPosition) // Return line positions
                )
            } else {
                // No match - select new tile
                Pair(
                    currentState.copy(selectedTiles = listOf(clickedPosition)),
                    null
                )
            }
        }
        else -> Pair(currentState, null)
    }
}

/**
 * Handle tile click logic
 */
private fun handleTileClick(
    currentState: GameState,
    clickedPosition: CellPosition
): GameState {
    val clickedTile = currentState.tiles.find { 
        it.position == clickedPosition && !it.isMatched 
    } ?: return currentState
    
    val selectedTiles = currentState.selectedTiles.toMutableList()
    
    return when {
        // Clicked already selected tile - deselect
        clickedPosition in selectedTiles -> {
            currentState.copy(
                selectedTiles = selectedTiles.apply { remove(clickedPosition) }
            )
        }
        // First selection
        selectedTiles.isEmpty() -> {
            currentState.copy(selectedTiles = listOf(clickedPosition))
        }
        // Second selection
        selectedTiles.size == 1 -> {
            val firstPosition = selectedTiles.first()
            val firstTile = currentState.tiles.find { it.position == firstPosition }
            
            if (firstTile != null && firstTile.patternType == clickedTile.patternType) {
                // Match found! (Simplified - in real game would check path)
                val updatedTiles = currentState.tiles.map { tile ->
                    if (tile.position == firstPosition || tile.position == clickedPosition) {
                        tile.copy(isMatched = true)
                    } else {
                        tile
                    }
                }
                currentState.copy(
                    tiles = updatedTiles,
                    selectedTiles = emptyList(),
                    matchedPairs = currentState.matchedPairs + 1,
                    score = currentState.score + 100,
                    isGameComplete = currentState.matchedPairs + 1 >= currentState.totalPairs
                )
            } else {
                // No match - select new tile
                currentState.copy(selectedTiles = listOf(clickedPosition))
            }
        }
        else -> currentState
    }
}

/**
 * Handle prop click logic
 * Returns Pair of (new GameState, showNoMatchDialog: Boolean)
 */
private fun handlePropClick(
    currentState: GameState,
    prop: GameProp
): Pair<GameState, Boolean> {
    val currentCount = currentState.props[prop] ?: 0
    if (currentCount <= 0) return Pair(currentState, false)
    
    val updatedProps = currentState.props.toMutableMap().apply {
        this[prop] = currentCount - 1
    }
    
    return when (prop) {
        GameProp.HINT -> {
            // Find a matchable pair that can actually connect
            val unmatchedTiles = currentState.tiles.filter { !it.isMatched }
            val patternGroups = unmatchedTiles.groupBy { it.patternType }
            
            // Try to find a pair that can actually connect via path
            var foundPair: Pair<GameTile, GameTile>? = null
            for (group in patternGroups.values) {
                if (group.size >= 2) {
                    // Check all combinations in this group
                    for (i in group.indices) {
                        for (j in i + 1 until group.size) {
                            val path = findPath(group[i].position, group[j].position, currentState)
                            if (path != null) {
                                foundPair = Pair(group[i], group[j])
                                break
                            }
                        }
                        if (foundPair != null) break
                    }
                }
                if (foundPair != null) break
            }
            
            if (foundPair != null) {
                // Found a connectable pair - select both tiles
                Pair(
                    currentState.copy(
                        selectedTiles = listOf(foundPair.first.position, foundPair.second.position),
                        props = updatedProps
                    ),
                    false
                )
            } else {
                // No connectable pairs found - don't consume the prop, show dialog
                Pair(currentState, true)
            }
        }
        GameProp.BOMB -> {
            // Remove selected tiles (simplified)
            if (currentState.selectedTiles.isNotEmpty()) {
                val updatedTiles = currentState.tiles.map { tile ->
                    if (tile.position in currentState.selectedTiles) {
                        tile.copy(isMatched = true)
                    } else {
                        tile
                    }
                }
                Pair(
                    currentState.copy(
                        tiles = updatedTiles,
                        selectedTiles = emptyList(),
                        props = updatedProps
                    ),
                    false
                )
            } else {
                Pair(currentState.copy(props = updatedProps), false)
            }
        }
        GameProp.SHUFFLE -> {
            // Shuffle unmatched tiles
            val unmatchedTiles = currentState.tiles.filter { !it.isMatched }
            val unmatchedPositions = unmatchedTiles.map { it.position }
            val shuffledPatterns = unmatchedTiles.map { it.patternType }.shuffled()
            
            val updatedTiles = currentState.tiles.map { tile ->
                if (!tile.isMatched) {
                    val index = unmatchedPositions.indexOf(tile.position)
                    tile.copy(patternType = shuffledPatterns[index])
                } else {
                    tile
                }
            }
            
            Pair(
                currentState.copy(
                    tiles = updatedTiles,
                    selectedTiles = emptyList(),
                    props = updatedProps
                ),
                false
            )
        }
    }
}

/**
 * Level completion dialog
 */
@Composable
private fun LevelCompleteDialog(
    currentLevel: Int,
    levelsToPass: Int,
    difficulty: Difficulty,
    onNextLevel: () -> Unit,
    onClose: () -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalStrings.current
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.5f)),
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .widthIn(min = 300.dp)
                .padding(24.dp)
                .clip(RoundedCornerShape(24.dp))
                .background(Color.White)
                .padding(24.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            // Success icon
            AppText(
                text = "🎉",
                style = MaterialTheme.typography.displayMedium
            )
            
            // Congratulations text
            AppText(
                text = strings.levelComplete,
                style = MaterialTheme.typography.headlineMedium,
                color = DeepGreen
            )
            
            // Level info
            AppText(
                text = strings.level.replace("%d", currentLevel.toString()),
                style = MaterialTheme.typography.titleMedium,
                color = DarkGray
            )
            
            // Reward info
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .background(
                        when (difficulty) {
                            Difficulty.EASY -> Green.copy(alpha = 0.15f)
                            Difficulty.HARD -> OrangeRed.copy(alpha = 0.15f)
                            Difficulty.EXPERT -> Color.Red.copy(alpha = 0.15f)
                            Difficulty.MASTER -> Color(0xFF8B00FF).copy(alpha = 0.15f)
                        }
                    )
                    .padding(horizontal = 12.dp, vertical = 6.dp)
            ) {
                AppText(
                    text = strings.levelProgress
                        .replaceFirst("%d", levelsToPass.toString())
                        .replaceFirst("%d", (currentLevel + levelsToPass).toString()),
                    style = MaterialTheme.typography.bodyMedium,
                    color = when (difficulty) {
                        Difficulty.EASY -> DeepGreen
                        Difficulty.HARD -> OrangeRed
                        Difficulty.EXPERT -> Color.Red
                        Difficulty.MASTER -> Color(0xFF8B00FF)
                    }
                )
            }
            
            Spacer(modifier = Modifier.height(8.dp))
            
            // Buttons
            Column(
                verticalArrangement = Arrangement.spacedBy(8.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Continue button
                PrimaryGameButton(
                    text = strings.nextLevel,
                    onClick = onNextLevel,
                    modifier = Modifier.fillMaxWidth()
                )
                
                // Exit button
                SecondaryGameButton(
                    text = strings.returnHome,
                    onClick = onClose,
                    modifier = Modifier.fillMaxWidth()
                )
            }
        }
    }
}
